<template>
  <div class="h-16 flex items-center justify-between py-2 px-3 gap-2">
    <div v-show="!searching" class="flex items-center grow gap-3 truncate">
      <template v-if="isSettingsOverview">
        <div
          @click="$emit('go-back')"
          class="flex gap-2 items-center text-white py-2"
        >
          <Icon name="back-arrow" size="xl" />
        </div>
      </template>
      <BuildingSelector
        v-if="!isSettingsOverview"
        :organisation="organisation"
        :buildings="buildings"
        variant="white"
      />

      <!-- View Selector -->
      <button
        v-if="
          type == 'tasks' ||
          type == 'files' ||
          type == 'assets' ||
          type == 'asset_groups'
        "
        :disabled="isEmpty(views)"
        class="flex items-center gap-1.5 truncate max-w-full grow"
        @click.stop="$emit('openViewSelector')"
      >
        <h1 class="pt-0.5 text-style-xl font-medium text-white truncate">
          {{ currentView?.name }}
        </h1>
        <Icon name="chevron-down" class="shrink-0 fg-white" aria-hidden />
      </button>
      <div v-else>
        <h1 class="pt-0.5 text-style-xl font-medium text-white truncate">
          {{
            isSettingsOverview
              ? type === "recurring_tasks"
                ? t("admin.navigation.recurring_tasks.label")
                : t("admin.navigation.settings.label")
              : t("admin.navigation." + type + ".label")
          }}
        </h1>
      </div>
    </div>
    <div v-show="searching" class="flex items-center grow">
      <ViewSearch
        ref="searchRef"
        :query="query"
        :placeholder="
          $t('components.inputs.search_in_view.placeholder', {
            view: currentView.name,
          })
        "
        @search="handleSearchChange($event?.target?.value || $event)"
        @close="handleCloseSearch"
      />
    </div>
    <div class="flex items-center">
      <Component
        v-for="icon in icons"
        :key="icon.id"
        :is="icon.separator ? Separator : IconButton"
        v-bind="
          icon.separator
            ? { orientation: 'vertical', decorative: true }
            : {
                variant: 'tertiary',
                type: 'white',
                'aria-label': icon.label,
              }
        "
        :class="icon.separator ? 'py-2 px-1.5 pointer-events-none' : 'p-2.5'"
        :disabled="icon.disabled"
        @click.stop="handleClick(icon.event)"
      >
        <div v-if="icon.separator" class="w-px h-8 bg-alpha-white-20" />
        <Icon v-else :name="icon.icon" size="xl" aria-hidden />
      </Component>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { isEmpty } from "lodash";
import { storeToRefs } from "pinia";
import { Separator } from "radix-vue";
import { IconButton } from "@/SharedComponents/Buttons";
import { BuildingSelector } from "@/SharedComponents/Navigation";
import { ViewSearch } from "@/SharedComponents/PageHeader/Partials";
import { Icon } from "@/SharedComponents/Icon";

import { useSearch } from "@/Composables/useSearch";
import { useI18n } from "vue-i18n";
import { useViewControls } from "@/Composables/useViewControls";

const { t } = useI18n();

const emit = defineEmits([
  "openViewSelector",
  "openNotifications",
  "openFilter",
]);

const props = defineProps({
  organisation: { type: String, required: true },
  buildings: { type: Array, default: () => [] },
  type: { type: String, required: true },
  views: { type: Array, default: () => [] },
  currentView: { type: Object, required: true },
  hasUnread: { type: Boolean, required: false },
  isSettingsOverview: { type: Boolean, required: false },
});

const searchStore = useSearch(props.type);
const { query } = storeToRefs(searchStore);
const { handleSearchChange, handleClearSearch } = searchStore;

// View Controls: Global
const viewControlsStore = useViewControls("viewControls");
const { loading } = storeToRefs(viewControlsStore);

const searchRef = ref(null);
const searching = ref(false);

watch(searching, (value) => {
  if (value) {
    nextTick(() => {
      searchRef.value?.focus();
    });
  }
});

const icons = computed(() => {
  if (props.type === "asset_groups" || props.type === "spaces") {
    return [
      {
        id: "notifications",
        icon: props.hasUnread ? "bell-bubble" : "bell-default",
        label: t("admin.settings.navigation.notifications.label"),
        event: "openNotifications",
        disabled: false,
      },
      {
        id: "separator",
        separator: true,
      },
      {
        id: "search",
        icon: "search",
        label: t("components.buttons.search"),
        event: "search",
        disabled: loading.value,
      },
    ];
  }

  if (
    props.type !== "tasks" &&
    props.type !== "files" &&
    props.type !== "assets" &&
    props.type !== "recurring_tasks"
  ) {
    return [
      {
        id: "notifications",
        icon: props.hasUnread ? "bell-bubble" : "bell-default",
        label: t("admin.settings.navigation.notifications.label"),
        event: "openNotifications",
        disabled: false,
      },
    ];
  }

  if (searching.value) {
    return [
      {
        id: "filter",
        icon: "filter-lines",
        label: t("components.buttons.filter"),
        event: "openFilter",
        disabled: loading.value,
      },
    ];
  }

  return [
    {
      id: "notifications",
      icon: props.hasUnread ? "bell-bubble" : "bell-default",
      label: t("admin.settings.navigation.notifications.label"),
      event: "openNotifications",
      disabled: false,
    },
    {
      id: "separator",
      separator: true,
    },
    {
      id: "search",
      icon: "search",
      label: t("components.buttons.search"),
      event: "search",
      disabled: loading.value,
    },
    {
      id: "filter",
      icon: "filter-lines",
      label: t("components.buttons.filter"),
      event: "openFilter",
      disabled: loading.value,
    },
  ];
});

function handleCloseSearch() {
  searching.value = false;
  handleClearSearch();
}

function handleClick(event) {
  if (event === "search") {
    searching.value = true;
    return;
  }
  emit(event);
}

onBeforeUnmount(() => {
  handleCloseSearch();
});
</script>
